import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';

/** GET /api/v1/ppjk-credits/month-usage/{accountCode}: this calendar month (WIB) so far. */
export type MonthUsage = {
  /** YYYY-MM. */
  month: string;
  totalAju: number;
  /** AJU per document label: PIB, PEB, … */
  byDocLabel: Record<string, number>;
  houseBl: number;
  manifests: number;
};

/** This month's usage for an account; nothing is fetched without a code. */
export default function useMonthUsage(accountCode?: string) {
  const [data, setData] = useState<MonthUsage | null>(null);
  const [loading, setLoading] = useState(Boolean(accountCode));
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = useCallback(
    async (signal?: AbortSignal) => {
      if (!accountCode) {
        setData(null);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data: res } = await axiosInstance.get<{ data: MonthUsage }>(apiUrl.credit.monthUsage(accountCode), {
          signal,
        });
        setData(res.data ?? null);
      } catch (err: unknown) {
        const e = err as { name?: string; code?: string; response?: { data?: { message?: string } } };
        if (e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
        setError(e.response?.data?.message || 'Gagal memuat penggunaan.');
      } finally {
        setLoading(false);
      }
    },
    [accountCode]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchUsage(controller.signal);
    return () => controller.abort();
  }, [fetchUsage]);

  return { data, loading, error, refetch: fetchUsage };
}
